import { Injectable } from "@angular/core";
import { ODataApiDataService } from "app/core/storage/odata-api-data.service";
import { LocalizedString } from "app/shared/model/localized-string.model";
import { Address, PhoneNumber } from "odq-toolbox";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";
import { environment } from "~env";
import { MemberSecondaryWorkAddress, PracticeType, PracticeTypeAddress, ProfessionalContext } from "./models/member-secondary-work-address.model";


const API_URL = environment.projectionQueryApi;

@Injectable({
  providedIn: "root",
})
export class MemberSecondaryWorkAddressQueryService {
  constructor(private dataService: ODataApiDataService<any>) { }

  getAll(memberId: string): Observable<any[]> {
    return this.dataService
      .odataList(
        `${API_URL}/memberWorkAddresses?$filter=MemberId eq ${memberId}&$orderby=EffectiveOn desc`
      )
      .pipe(
        map((res) => {
          return res.map((item) => {
            return this.parseWorkAddress(item);
          });
        })
      );
  }

  parseWorkAddress(item: any): MemberSecondaryWorkAddress {
    return {
      id: item.Id,
      effectiveOn: item.EffectiveOn,
      effectiveUntil: item.EffectiveUntil,
      isCurrent: item.IsCurrent,
      companyName: item.CompanyName,
      phone: new PhoneNumber(
        item.PhoneCountryCode ?? 1,
        item.PhoneNumber,
        item.PhoneExt
      ),
      phone2: new PhoneNumber(
        item.Phone2CountryCode ?? 1,
        item.Phone2Number,
        item.Phone2Ext
      ),
      fax: new PhoneNumber(
        item.FaxCountryCode ?? 1,
        item.FaxNumber,
        item.FaxExt
      ),
      address: new Address(
        item.NormalizeId,
        item.Line1,
        item.Line2,
        item.City,
        item.ProvinceCode,
        item.PostalCode,
        item.CountryCode
      ),
      wheelchairAccess: item.WheelchairAccess,
      practiceType: this.parsePracticeType(item),
      clinicId: item.ClinicId,
      clinicName: item.ClinicName,
      clinicType: item.ClinicType,
      isAssociate: item.IsAssociate,
      isCompany: item.IsCompany,
      isEmployee: item.IsEmployee,
      isSelfEmployed: item.IsSelfEmployed,
      isPrincipal: item.IsPrincipal,
      memberId: item.MemberId._value || item.MemberId,
      otherPracticeType: item.OtherPracticeType,
      otherPracticeTypeAddress: item.OtherPracticeTypeAddress,
      otherProfessionalContext: item.OtherProfessionalContext,
      practiceTypeAddress: this.parsePracticeTypeAddress(item),
      professionalContext: this.parseProfessionalContext(item),
      municipalityCode: item.MunicipalityCode
    };
  }

  parsePracticeType(item: any): PracticeType {
    if (item.PracticeTypeId === null)
      return null;

    let practice: PracticeType = {
      id: item.PracticeTypeId,
      code: item.PracticeTypeCode,
      description: new LocalizedString(
        item.PracticeTypeFr,
        item.PracticeTypeEn
      ),
    }

    return practice;
  }

  parsePracticeTypeAddress(item: any): PracticeTypeAddress {

    if (item.PracticeTypeAddressId === null)
      return null;

    let practiceTypeAddress: PracticeTypeAddress = {
      id: item.PracticeTypeAddressId,
      code: item.PracticeTypeAddressCode,
      description: new LocalizedString(
        item.PracticeTypeAddressFr,
        item.PracticeTypeAddressEn
      )
    };

    return practiceTypeAddress;
  }

  parseProfessionalContext(item: any): ProfessionalContext {
    if (item.ProfessionalContextId === null)
      return null;

    let professionalContext: ProfessionalContext = {
      id: item.ProfessionalContextId,
      code: item.ProfessionalContextCode,
      description: new LocalizedString(
        item.ProfessionalContextFr,
        item.ProfessionalContextEn
      )
    }

    return professionalContext;
  }

}
